/*
This file handles all the basic login/logout functionality, including hydration of the user data in the profile modal on
each page load if the user is logged in.
 */

import WindCreekClient from './WindCreekClient.mjs';
import UserAccount from './UserAccount.mjs';
import { query, queryAll, showElement, hideElement } from '../utils.mjs';
import {
  Rewards,
  findPointsToNextToken,
  getNextEarnedPrimeTokenAmount,
  getCurTierFromPoints,
  getNextTierFromPoints
} from './Rewards.mjs';

UserAccount.windCreekClient = new WindCreekClient();

const loginTypes = UserAccount.getLoginTypes();

let loginForms = {
  default: query(`#wc-rewards-signin-modal [data-user-login-form][data-login-type="${loginTypes.EMAIL_PASSWORD}"`),
  alt: query(`#wc-rewards-signin-modal [data-user-login-form][data-login-type="${loginTypes.REWARDS_NUMBER_PIN}"`),
};

/*
This function populates the profile data on the page. This includes the profile modal opened by the profile button in
the header, plus some other common profile elements displayed in various places on the account pages.
*/
function setupProfile(accountData, profileData, rewards) {
  if (typeof newrelic === 'object') {
    newrelic.setCustomAttribute('wc_user_account_number', profileData.patronAccountNumber);
    newrelic.setUserId(profileData.patronAccountNumber);
  }

  const currentYTDTier = getCurTierFromPoints(profileData.basePointsEarned);
  const nextYTDTier = getNextTierFromPoints(profileData.basePointsEarned);
  const pointsNeededYTD = nextYTDTier.min - profileData.basePointsEarned;

  // Populate all the common profile elements that are displayed on the various account pages
  queryAll('[data-profile-name]').forEach(element => {
    element.textContent = UserAccount.getDisplayName(accountData, profileData);
  });
  queryAll('[data-profile-win-loss-name]').forEach(element => {
    element.textContent = UserAccount.getName(profileData);
  });
  queryAll('[data-rewards-number]').forEach(element => {
    element.textContent = profileData.patronAccountNumber;
  });
  queryAll('[data-tier="current"]').forEach(element => {
    element.setAttribute('data-tier', profileData.clubState.toLowerCase());
  });
  queryAll('[data-tier="next"]').forEach(element => {
    element.setAttribute('data-tier', profileData.nextClubState.toLowerCase());
  });
  queryAll('[data-tier="current-ytd"]').forEach(element => {
      element.setAttribute('data-tier', currentYTDTier.label.toLowerCase());
  });
  queryAll('[data-tier="next-ytd"]').forEach(element => {
    if (UserAccount.isOnHighestTier(profileData)) {
      element.setAttribute('data-tier', 'Unknown');
    }
    else {
      element.setAttribute('data-tier', nextYTDTier.label.toLowerCase());
    }
  });
  queryAll('[data-tier-name]').forEach(element => {
    element.textContent = profileData.clubState;
  });
  queryAll('[data-next-tier-name]').forEach(element => {
    element.textContent = nextYTDTier.label;
  });
  queryAll('[data-points-needed]').forEach(element => {
    if (UserAccount.isOnHighestTier(profileData)) {
      element.textContent = findPointsToNextToken(profileData.basePointsEarned).toLocaleString();
    } else {
      element.textContent = profileData.pointsNeeded.toLocaleString();
    }
  });
  queryAll('[data-points-needed-ytd]').forEach(element => {
    if (UserAccount.isOnHighestTier(profileData)) {
      element.textContent = findPointsToNextToken(profileData.basePointsEarned).toLocaleString();
    } else {
      element.textContent = pointsNeededYTD.toLocaleString();
    }
  });
  queryAll('[data-next-tier-earned-prime-tokens]').forEach(element => {
    let nextEarnedPrimeTokenAmount = getNextEarnedPrimeTokenAmount(profileData);
    element.textContent = `${nextEarnedPrimeTokenAmount} Prime Token${nextEarnedPrimeTokenAmount > 1 ? 's' : ''}`;
  });

  if (UserAccount.isOnHighestTier(profileData)) {
    hideElement("[data-hide-on-fantasy]");
    showElement("[data-show-on-fantasy]");
  }

  let filledPointsCircles = queryAll('[data-current-points-circle] [data-points-circle]');
  filledPointsCircles.forEach(element => {
    element.setAttribute('max', 100);
    element.setAttribute('value', 100);
  });

  let pointsCircles = queryAll('.progress-next-ytd [data-points-circle]');
  let maxCircleValue;
  if (UserAccount.isOnHighestTier(profileData)) {
    maxCircleValue = profileData.basePointsEarned + findPointsToNextToken(profileData.basePointsEarned);
  } else {
    const nextTier = getNextTierFromPoints(profileData.basePointsEarned);
    maxCircleValue = nextTier.min;
  }

  pointsCircles.forEach(element => {
    element.setAttribute('max', maxCircleValue);
    element.setAttribute('value', profileData.basePointsEarned);
  });

  let rewardsCount = 0;
  let rewardsGroupedByStatus = [];
  if (rewards?.rewardsWithTimeZone?.patronRewards || rewards?.rewardsWithTimeZone?.partnerRewards) {
    rewardsGroupedByStatus = Rewards.groupByStatus(rewards?.rewardsWithTimeZone?.patronRewards, rewards?.rewardsWithTimeZone?.partnerRewards);
    rewardsCount = rewardsGroupedByStatus?.available?.length;
  }
  if (UserAccount.signedInModalElm) {
    UserAccount.signedInModalElm.querySelector('[data-available-rewards-count]').textContent = rewardsCount ?? 0;
  }

  if (location.search.toLowerCase().match(/login-success=true/)) {
    UserAccount.successfulSignInToast.show();
  }
}

if (UserAccount.isLoggedIn()) {
  // The user has an existing session. Call the API to retrieve the correct profile and reward data based on the login type.
  if (UserAccount.getLoginType() === loginTypes.EMAIL_PASSWORD) {
    Promise.all([UserAccount.getAccountData(), UserAccount.getProfile(), UserAccount.getRewards()])
    .then(([accountData, profileData, rewardsData]) => {
        setupProfile(accountData, profileData, rewardsData);
        UserAccount.setStatusLoggedIn();
      })
      .catch(error => {
        console.log('unable to load account, profile, or reward data');
        console.error(error);
        newrelic.noticeError(error);
      });
  } else {
    Promise.all([UserAccount.getProfile(), UserAccount.getRewards()])
      .then(([profileData, rewardsData]) => {
        let accountData = {}; // Logging in with rewards number + PIN doesn't grant access to the online account data, so set this to an empty object
        setupProfile(accountData, profileData, rewardsData);
        UserAccount.setStatusLoggedIn();
      })
      .catch(error => {
        console.log('unable to load profile or reward data');
        console.error(error);
        newrelic.noticeError(error);
      });
  }
} else { // User is not logged in
  // Display the login modal if login=true is passed in the URL parameters
  if (location.search.toLowerCase().match(/login=true/) !== null) {
    UserAccount.signInModal.show();

    // If the user was trying to access a specific account page, show a warning about requiring authentication
    let redirect = location.search.toLowerCase().match(/redirect=([^&]+)/);
    if (redirect?.length === 2) {
      showElement('[data-login-required-message]');
    }
  }
}

// Handle the form submit event for all the login forms

// The Miami site doesn't have all the same login forms as the main site, so remove the empty ones
for (let loginType in loginForms) {
  if (loginForms[loginType] === null) {
    delete loginForms[loginType];
  }
}
Object.values(loginForms).forEach(loginForm => loginForm.addEventListener('submit', event => {
  event.preventDefault();

  // Hide any error messages that were displayed due to a previous (failed) login attempt
  hideElement('[data-unknown-email-message]');
  hideElement('[data-incorrect-password-message]');
  hideElement('[data-alt-login-message]');
  hideElement('[data-general-login-error]');
  hideElement('[data-account-locked-message]');
  hideElement('[data-successful-signout-message]');
  hideElement('[data-login-required-message]');
  hideElement('[data-cmp-role-message]');
  hideElement('[data-pls-role-message]');

  let formData = new FormData(event.target);
  let loginType = event.target.getAttribute('data-login-type');
  let identity = loginType === loginTypes.EMAIL_PASSWORD ? formData.get('email') : formData.get('account-number');
  let credential = loginType === loginTypes.EMAIL_PASSWORD ? formData.get('password') : formData.get('pin');

  if (!identity) {
    // TODO: email/rewards number field is required, show a message? right now we're just using the native browser validation
  }

  if (!credential) {
    // TODO: password/PIN field is required, show a message? right now we're just using the native browser validation
  }

  if (typeof newrelic === 'object') {
    newrelic.setCustomAttribute('wc_login_attempt_identity', identity);
  }

  let specificMessageVisible = false;
  event.target.classList.add('loading-animation');
  let userData = null;
  UserAccount.login(identity, credential, loginType)
    .then(result => {
      if (result.success === true) {
        userData = result.user;
      } else {
        // Handle login errors by displaying the appropriate alert to the user and logging the details to the console
        // Also, throw a new error to stop the promise chain from continuing
        switch (result) {
          case 'UNKNOWN_EMAIL':
            showElement('[data-unknown-email-message]');
            specificMessageVisible = true;
            throw new Error('Unknown email');
          case 'INCORRECT_PASSWORD':
            showElement('[data-incorrect-password-message]');
            specificMessageVisible = true;
            throw new Error('Incorrect password');
          case 'USER_LOCKED_OUT':
            showElement('[data-account-locked-message]');
            specificMessageVisible = true;
            throw new Error('User locked out');
          case 'MISSING_REQUIRED_ROLE':
            showElement('[data-cmp-role-message]');
            showElement('[data-pls-role-message]');
            specificMessageVisible = true;
            throw new Error('Invalid account type for this site (missing required role)');
          case 'Invalid Pin or Account Number':
            /*
            TODO: Submitting a rewards number or PIN with the incorrect format (e.g. wrong number of digits) causes
             an API error which displays the generic error message rather than the invalid PIN/account number message.
             Should we check for correct formatting on our end before submitting so we can display the right message?
            */
            showElement('[data-alt-login-message]');
            specificMessageVisible = true;
            throw new Error('Invalid pin or account number');
          default:
            // Unknown error - display a generic message

            /* TODO: Extra logic to decide whether to use the error message from the API response (if it's user friendly), or not (if it's a developer message). This is not working 100% yet!
            // Only display the message to the user if it's a user friendly string, not an error string like INCORRECT_PASSWORD
            let generalLoginErrorAlertText = query('[data-general-login-error] .alert-text');
            if (typeof result === 'string' && result.length > 0 && result.match(/^[A-Z_]+$/) === null) {
              // Save the default message in case we need to restore it, e.g. if a subsequent login attempt fails for a different (generic) reason
              let originalMessage = generalLoginErrorAlertText.innerText;
              if (result !== originalMessage) {
                generalLoginErrorAlertText.setAttribute('data-original-message', originalMessage);
              }

              generalLoginErrorAlertText.textContent = result;
            } else {
              // The API didn't return a useful message, so restore our default generic message (if necessary)
              let originalMessage = generalLoginErrorAlertText.getAttribute('data-original-message');

              if (originalMessage !== null && originalMessage !== '') {
                generalLoginErrorAlertText.textContent = originalMessage;
                generalLoginErrorAlertText.setAttribute('data-original-message', '');
              }
            }
            */

            throw new Error('User login failed. Check the network tab for details on the API call.');
        }
      }
    })
    .then(() => {
        /*
        Getting all the necessary data requires multiple API calls, so we need to wait for everything to finish
        (i.e. wait for all the promises to resolve)
        */
        let promiseArray = [];
        if (UserAccount.getLoginType() === loginTypes.EMAIL_PASSWORD) {
          promiseArray = [
            userData ?? UserAccount.getAccountData(), // We might already have this data, because it's returned by a successful login call
            UserAccount.getProfile(),
            UserAccount.getRewards(),
          ];
        } else {
          promiseArray = [
            {}, // If the user logs in with rewards number + PIN, they don't have access to the online social casino user data
            UserAccount.getProfile(),
            UserAccount.getRewards(),
          ];
        }

        Promise.all(promiseArray)
          .then(([accountData, profileData, rewardsData]) => {
            setupProfile(accountData, profileData, rewardsData);
            UserAccount.setStatusLoggedIn(true);
            UserAccount.signInModal.hide();

            // If the user was trying to access a specific account page, redirect them now
            if (location.search.toLowerCase().match(/login=true/) !== null) {
              let redirect = location.search.toLowerCase().match(/redirect=([^&]+)/);
              if (redirect.length === 2) {
                let redirectPath = decodeURIComponent(redirect[1]);
                if (!redirectPath.startsWith('/')) {
                  redirectPath = '/' + redirectPath;
                }
                location.href = redirectPath + '?login-success=true';
              }
            } else if (UserAccount.isMiamiSite()) {
              location.href = '/my-rewards/?login-success=true';
            } else {
              // If we don't have to do a redirect, just show the profile modal
              // 500ms timeout is to ensure the sign in modal has finished closing
              setTimeout(() => {
                UserAccount.signedInModal.show();
              }, 500);
            }

            event.target.classList.remove('loading-animation');
          })
          .catch(error => {
            event.target.classList.remove('loading-animation');
            console.log('unable to load account, profile, or reward data');
            console.error(error); // Rethrow the inner error to stop the promise chain from continuing
            newrelic.noticeError(error);
          });
    })
    .catch(error => {
      event.target.classList.remove('loading-animation');
      if (specificMessageVisible === false && UserAccount.maintenanceModeEnabled === false) {
        showElement('[data-general-login-error]');
      }
      console.log('error occurred during login');
      console.error(error); // Rethrow the inner error to stop the promise chain from continuing
      newrelic.noticeError(error);
    });
}));

// Click event for the button that toggles the login type. This controls which fields are available on the login form.
// Email + Password or Rewards Number + PIN
queryAll('#wc-rewards-signin-modal [data-toggle-login-type]').forEach(toggleLoginTypeButton => toggleLoginTypeButton.addEventListener('click', event => {
  event.preventDefault();

  if (loginForms.default.hasAttribute('hidden')) {
    hideElement(loginForms.alt);
    showElement(loginForms.default);
  } else {
    hideElement(loginForms.default);
    showElement(loginForms.alt);
  }

  let loginTypeToggleLabel = query('#wc-rewards-signin-modal [data-login-type-toggle-label]');
  let newText = loginTypeToggleLabel.getAttribute('data-toggle-text');
  let oldText = loginTypeToggleLabel.innerText;
  loginTypeToggleLabel.textContent = newText;
  loginTypeToggleLabel.setAttribute('data-toggle-text', oldText);
}));

// Handle the click event for all sign out buttons
queryAll('[data-sign-out]').forEach(signOutButton => signOutButton.addEventListener('click', event => {
  event.preventDefault();
  UserAccount.logout();
  if (UserAccount.signedInModalElm) {
    UserAccount.signedInModal.hide();
  }
}));

UserAccount.signInModalElm.addEventListener('hide.bs.modal', event => {
  event.target.querySelectorAll('input[type="password"]').forEach(passwordElement => {
    passwordElement.value = '';
  });
});
